import type { DashboardLanguage, DashboardLanguagePreference } from "../types";

export const DASHBOARD_LANGUAGE_STORAGE_KEY = "appLanguage";
export const DASHBOARD_LANGUAGE_PREFERENCE_STORAGE_KEY = "appLanguagePreference";
/** Set when the user picks a language in Profile settings; a detected language never sets it. */
export const DASHBOARD_LANGUAGE_CHOSEN_STORAGE_KEY = "appLanguageChosen";
export const DASHBOARD_LANGUAGE_CHANGE_EVENT = "dashboard-language-change";
export const DEFAULT_DASHBOARD_LANGUAGE: DashboardLanguage = "en";
/** In the order Profile settings lists them. */
export const SUPPORTED_DASHBOARD_LANGUAGES = ["en", "da", "sv"] as const;

/** `byLanguage`'s value for `language`, or its English one when that language has none. */
export function pickForDashboardLanguage<ByLanguage extends { en: unknown }>(
  byLanguage: ByLanguage,
  language: DashboardLanguage,
): ByLanguage[keyof ByLanguage] {
  const values = byLanguage as Partial<Record<DashboardLanguage, ByLanguage[keyof ByLanguage]>>;
  return values[language] ?? (byLanguage.en as ByLanguage[keyof ByLanguage]);
}

/** BCP 47 locale for dates/numbers in dashboard chrome. */
export function dashboardIntlLocale(language: DashboardLanguage): string {
  if (language === "da") return "da-DK";
  if (language === "sv") return "sv-SE";
  return "en-GB";
}

export function normalizeDashboardLanguage(value: unknown): DashboardLanguage | null {
  const baseLanguage = String(value || "")
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];

  return (SUPPORTED_DASHBOARD_LANGUAGES as readonly string[]).includes(baseLanguage)
    ? (baseLanguage as DashboardLanguage)
    : null;
}

export function normalizeDashboardLanguagePreference(
  value: unknown,
): DashboardLanguagePreference | null {
  return normalizeDashboardLanguage(value);
}

function readIntlLocale(intlObject: typeof Intl | undefined): string {
  try {
    return intlObject?.DateTimeFormat?.().resolvedOptions?.().locale || "";
  } catch {
    return "";
  }
}

export function detectDashboardLanguage({
  navigatorObject,
  intlObject,
}: {
  navigatorObject?: Navigator;
  intlObject?: typeof Intl;
} = {}): DashboardLanguage {
  const resolvedNavigator =
    navigatorObject || (typeof navigator !== "undefined" ? navigator : undefined);
  const resolvedIntl = intlObject || (typeof Intl !== "undefined" ? Intl : undefined);
  const candidates = [
    ...(Array.isArray(resolvedNavigator?.languages) ? resolvedNavigator.languages : []),
    resolvedNavigator?.language,
    readIntlLocale(resolvedIntl),
  ];

  for (const candidate of candidates) {
    const language = normalizeDashboardLanguage(candidate);
    if (language) {
      return language;
    }
  }

  return DEFAULT_DASHBOARD_LANGUAGE;
}

export function readDashboardLanguagePreference(
  storage = typeof localStorage !== "undefined" ? localStorage : null,
  {
    navigatorObject,
    intlObject,
  }: {
    navigatorObject?: Navigator;
    intlObject?: typeof Intl;
  } = {},
): DashboardLanguagePreference {
  try {
    const rawPreference = storage?.getItem(DASHBOARD_LANGUAGE_PREFERENCE_STORAGE_KEY);
    if (rawPreference === "system") {
      return detectDashboardLanguage({ navigatorObject, intlObject });
    }

    const storedPreference =
      normalizeDashboardLanguagePreference(rawPreference) ??
      normalizeDashboardLanguage(storage?.getItem(DASHBOARD_LANGUAGE_STORAGE_KEY));
    if (storedPreference) {
      // Before Swedish was offered, every load stored the detected language, so a Swedish browser
      // stored English. English the user did not pick follows a browser now detected as Swedish.
      if (
        storedPreference === "en" &&
        storage?.getItem(DASHBOARD_LANGUAGE_CHOSEN_STORAGE_KEY) !== "true" &&
        detectDashboardLanguage({ navigatorObject, intlObject }) === "sv"
      ) {
        return "sv";
      }
      return storedPreference;
    }
  } catch {
    // Fall through to default handling.
  }

  return detectDashboardLanguage({ navigatorObject, intlObject });
}

export function resolveDashboardLanguage(
  preference: DashboardLanguagePreference,
): DashboardLanguage {
  return preference;
}

/** Stores the language the user picked, which detection never overrides. */
export function persistChosenDashboardLanguage(
  language: DashboardLanguage,
  storage = typeof localStorage !== "undefined" ? localStorage : null,
): void {
  persistDashboardLanguagePreference(language, language, storage);
  try {
    storage?.setItem(DASHBOARD_LANGUAGE_CHOSEN_STORAGE_KEY, "true");
  } catch {
    // Persisting is best-effort (e.g. storage disabled).
  }
}

export function persistDashboardLanguagePreference(
  preference: DashboardLanguagePreference,
  resolvedLanguage: DashboardLanguage = preference,
  storage = typeof localStorage !== "undefined" ? localStorage : null,
): void {
  try {
    storage?.setItem(DASHBOARD_LANGUAGE_PREFERENCE_STORAGE_KEY, preference);
    storage?.setItem(DASHBOARD_LANGUAGE_STORAGE_KEY, resolvedLanguage);
  } catch {
    // Persisting is best-effort (e.g. storage disabled).
  }
}

/** @deprecated Use DashboardLanguageProvider instead. Kept for legacy callers/tests. */
export function readDashboardLanguage(
  storage = typeof localStorage !== "undefined" ? localStorage : null,
): DashboardLanguage | null {
  try {
    return normalizeDashboardLanguage(storage?.getItem(DASHBOARD_LANGUAGE_STORAGE_KEY));
  } catch {
    return null;
  }
}

/** @deprecated Use DashboardLanguageProvider instead. Kept for legacy callers/tests. */
export function initializeDashboardLanguage({
  storage = typeof localStorage !== "undefined" ? localStorage : null,
  navigatorObject,
  intlObject,
}: {
  storage?: Storage | null;
  navigatorObject?: Navigator;
  intlObject?: typeof Intl;
} = {}): DashboardLanguage {
  const preference = readDashboardLanguagePreference(storage, { navigatorObject, intlObject });
  persistDashboardLanguagePreference(preference, preference, storage);
  return preference;
}
